<template>
   <!-- [theme class css in cms-themes.scss] -->
   <div :class="`theme-${containerTheme}`">
      <div>
         <div class="flex flex-col items-center text-center">
            <div
               class="mb-16 px-4 pt-16 xl:mb-20"
               :class="[{ 'pb-20 md:pb-16 xl:py-20': !!image }, { 'pb-2 lg:pb-4 xl:pb-8 xl:pt-24': !image }]"
            >
               <Kicker v-if="kicker" class="border-current" :text="kicker" />

               <h1
                  class="mx-auto mb-8 mt-5 max-w-sm text-3xl tracking-tight md:leading-tight md:tracking-normal lg:mb-7 lg:mt-5 lg:text-4xl"
               >
                  {{ heading }}
               </h1>

               <p v-if="description" class="mx-auto max-w-sm py-2 text-base leading-snug lg:text-lg">
                  {{ description }}
               </p>

               <PageCTAItems
                  v-if="ctas && ctas.length > 0"
                  :ctas="ctas"
                  :theme="containerTheme"
                  class="mt-4 justify-center"
                  :isPageCta="true"
               />
            </div>
         </div>
      </div>
   </div>
   <div v-if="image" class="container mx-auto">
      <LazyLoadingImage
         class="hero-ratios -mt-20 w-full overflow-hidden rounded-2xl bg-grey-10 md:-mt-16 md:h-[25rem] md:rounded-3xl lg:-mt-16 lg:h-[30rem] xl:-mt-20 xl:h-[35rem]"
      >
         <!--
            Uses the picture element (https://web.dev/learn/design/picture-element/) to display an image at various viewport sizes. They match the breakpoints set in the Tailwind config: md: "768px", lg: "992px", xl: "1280px"
            We use pixels for importing images, because the images we import are of fixed size.
            TODO: switch to cloudinary style & allow for 1x and 2x res. Check that we need 4 separate source sizes at that point.
         -->
         <picture class="absolute left-1/2 top-1/2 size-full -translate-x-1/2 -translate-y-1/2">
            <source :srcset="image.url.lg" media="(min-width: 1280px)" />
            <source :srcset="image.url.md" media="(min-width: 992px)" />
            <source :srcset="image.url.sm" media="(min-width: 576px)" />
            <img
               :srcset="image.url.xs"
               :alt="image.altTag"
               decoding="async"
               class="min-h-full min-w-full object-cover"
            />
         </picture>
      </LazyLoadingImage>
   </div>
</template>

<script setup lang="ts">
//types
import type { CTAItemData } from "@/components/ui/buttons/PageCTAItems.vue";
import { Theme, type ImageData, type ContainerTheme } from "@/types";

// props
// -----------------------------------------
const props = withDefaults(
   defineProps<{
      heading: string;
      image?: ImageData;
      kicker?: string;
      description?: string;
      ctas?: CTAItemData[];
      theme?: ContainerTheme;
   }>(),
   {
      theme: Theme.BrandDark,
   }
);

const containerTheme = computed(() => (props.theme ? props.theme : Theme.BrandDark));
</script>

<style scoped lang="scss">
.hero-ratios {
   // Set fixed ratios for this element on mobile (on desktop we can just use a fixed height)
   // This avoids page layout reflows on image load as the correct space should be held by the img container
   @media (max-width: 47.9375rem) {
      padding-top: 61.88%;
   }
}
</style>
